import { useState } from "react";
import InfoTip from "@/shared/components/infoTip/InfoTip";

export type CostRow = {
  label: string;
  people: string;
  touches: string;
  rate: string;
  cost: string;
};

export type ApprovalState = "not_sent" | "awaiting" | "approved" | "changes";

const APPROVAL_COPY: Record<ApprovalState, { label: string; tone: string; note: string }> = {
  not_sent: {
    label: "Not sent",
    tone: "off",
    note: "Nothing has gone to the client yet.",
  },
  awaiting: {
    label: "Awaiting client",
    tone: "blocked",
    note: "The client has the copy and has not answered yet.",
  },
  approved: {
    label: "Approved",
    tone: "on",
    note: "The client approved this copy. Edits send it back for approval.",
  },
  changes: {
    label: "Changes requested",
    tone: "blocked",
    note: "The client asked for changes. Their notes are on each touch.",
  },
};

/* The footer's (i) explains all four states, not just the one showing (MPN-2207): Ops
   reading "Awaiting client" wants to know what the next state will mean, and the pill
   alone never says. */
const STATE_LEGEND: { state: ApprovalState; line: string }[] = [
  { state: "not_sent", line: "Nothing has gone to the client." },
  { state: "awaiting", line: "Sent. The client has not answered." },
  { state: "approved", line: "The client approved it. Touches send on schedule." },
  { state: "changes", line: "The client asked for changes. Notes sit on each touch." },
];

type Props = {
  costRows: CostRow[];
  costTotal: string;
  costContracted: string;
  /** Set when the estimate exceeds the contracted line, with the overage sentence. */
  costOverText?: string;
  approval: ApprovalState;
  sendLabel: string;
  sendDisabled: boolean;
  previewDisabled: boolean;
  onPreview: () => void;
  onSend: () => void;
  onOpenContract: () => void;
};

export function ApprovalFooter({
  costRows,
  costTotal,
  costContracted,
  costOverText,
  approval,
  sendLabel,
  sendDisabled,
  previewDisabled,
  onPreview,
  onSend,
  onOpenContract,
}: Props) {
  const [open, setOpen] = useState(false);
  const copy = APPROVAL_COPY[approval];

  return (
    <div className="esms-footer">
      {open && (
        <div className="esms-footer__breakdown">
          <div className="esms-footer__scroll">
            <div className="esms-footer__table">
              <div className="esms-footer__th">
                <span>Channel</span>
                <span>People</span>
                <span>Touches</span>
                <span>Rate</span>
                <span>Cost</span>
              </div>
              {costRows.map((row) => (
                <div className="esms-footer__tr" key={row.label}>
                  <span>{row.label}</span>
                  <span>{row.people}</span>
                  <span>{row.touches}</span>
                  <span>{row.rate}</span>
                  <span>{row.cost}</span>
                </div>
              ))}
              <div className="esms-footer__total">
                <span>Total</span>
                <span />
                <span />
                <span />
                <span>{costTotal}</span>
              </div>
            </div>
          </div>
          <div className="esms-footer__note">
            People × touches that will send × rate. Off and blocked touches are left out. SMS
            counts segments. An estimate: the real count depends on who is still eligible on each
            send date.
          </div>
        </div>
      )}

      {costOverText && (
        <div style={{ padding: "10px 24px 0" }}>
          <div className="esms-banner">
            <span className="esms-banner__icon">!</span>
            <span className="esms-banner__text">
              <strong>Over the contracted amount.</strong> {costOverText}
            </span>
            <button type="button" className="esms-footer__over-link" onClick={onOpenContract}>
              Open Contract
            </button>
          </div>
        </div>
      )}

      <div className="esms-footer__bar">
        <span className="esms-footer__cost">
          <span>Est. delivery cost</span>
          <InfoTip
            label="Est. delivery cost"
            text="Fixed contracted lines for email and SMS, separate from media budget. Set on Contract."
          />
          <strong className="esms-footer__cost-total">{costTotal}</strong>
          <span className="esms-footer__cost-of">of {costContracted} contracted</span>
          <button
            type="button"
            className="esms-footer__chev"
            aria-expanded={open}
            aria-label="Cost breakdown"
            onClick={() => setOpen((v) => !v)}
          >
            <i className={open ? "bi bi-chevron-down" : "bi bi-chevron-up"} />
          </button>
        </span>

        <span className="esms-footer__divider" />

        <span className="esms-footer__approval">
          <span className="esms-footer__approval-label">Content approval</span>
          <span className={`touch-pill touch-pill--${copy.tone}`}>{copy.label}</span>
          <InfoTip
            label="Content approval"
            placement="above"
            text={
              <span className="esms-footer__legend">
                {STATE_LEGEND.map((row) => (
                  <span
                    key={row.state}
                    className={
                      row.state === approval
                        ? "esms-footer__legend-row esms-footer__legend-row--now"
                        : "esms-footer__legend-row"
                    }
                  >
                    <b>{APPROVAL_COPY[row.state].label}</b> — {row.line}
                  </span>
                ))}
              </span>
            }
          />
        </span>

        <span className="esms-footer__spacer" />

        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          disabled={previewDisabled}
          onClick={onPreview}
        >
          Preview client view
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={sendDisabled}
          onClick={onSend}
        >
          {sendLabel}
        </button>
      </div>
    </div>
  );
}

export default ApprovalFooter;
